import './Tournaments.css'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { Link } from 'react-router-dom'

import { useGetCommunityQuery } from '../../redux/communityService/communityApiSlice'
import { useGetPlayersQuery } from '../../redux/playersService/playersApiSlice.js'
import { ReactComponent as CalendarIcon } from '../../icon/calendar.svg'
import { Header } from '../../components/Header/Header'
import { Popup } from '../../components/Popup/Popup'
import { Calendar } from '../../components/Calendar/Calendar'
import { TournamentsList } from '../../components/TournamentsList/TournamentsList'
import { ReactComponent as TournamentAddition } from '../../icon/add_tournaments.svg'
import { ReactComponent as Cross } from '../../icon/cross.svg'
import { BottomMenu } from '../../components/BottomMenu/BottomMenu'
import { TournamentForm } from '../../components/TournamentForm/TournamentForm'
import GlobalStore from '../../GlobalStore'

const Tournaments = () => {
	sessionStorage.clear()
	const today = new Date()
	const { t } = useTranslation()
	const { communityUid, communityName } = GlobalStore.getStore()
	const { data = {}, isLoading, error } = useGetCommunityQuery(
		{ community_uid: communityUid }, { skip: !communityUid }
	)

	const [popupIsOpen, setPopupIsOpen] = useState(false)
	const [tournaments, setTournaments] = useState([])
	const [filterPlaceholder, setFilterPlaceholder] = useState(t('Filter by date'))
	const [startDate, setStartDate] = useState(today)
	const [endDate, setEndDate] = useState(today)
	const [createNewTournament, setCreateNewTournament] = useState(false)
	const [nextRequest, setNextRequest] = useState(false)

	useEffect(() => {
		if (Object.keys(data).length !== 0) {
			setNextRequest(true)
			setTournaments(data?.tournaments)
		}
	}, [data])

	// Получаем всех игроков сообщества
	const {
		data: playersData = [],
		isLoading: playersLoading
	} = useGetPlayersQuery({ community_uid: communityUid }, { skip: !nextRequest || isLoading })

	const handleClose = useCallback(() => {
		setPopupIsOpen(false)
	}, [])

	const resetDate = useCallback(() => {
		setStartDate(today)
		setEndDate(today)
		setTournaments(data?.tournaments)
		setFilterPlaceholder('Filter by date')

		handleClose()
	}, [data?.tournaments, today, handleClose])

	const handleOpen = useCallback(() => {
		setPopupIsOpen(true)
	}, [])

	const dateFilter = useCallback(() => {
		const stDate = new Date(startDate)
		const stFormatDate = `${stDate.getDate()}.${stDate.getMonth() + 1}.${stDate.getFullYear()}`

		const ndDate = new Date(endDate)
		const ndFormatDate = `${ndDate.getDate()}.${ndDate.getMonth() + 1}.${ndDate.getFullYear()}`

		const filtredTournaments = data?.tournaments?.filter((item) => {
			const itemDate = new Date(item.start_date)
			const filterStartDate = new Date(startDate)
			filterStartDate.setHours(0, 0, 0, 0)

			const filterEndDate = new Date(endDate)
			filterEndDate.setHours(23, 59, 59, 999)

			return itemDate >= filterStartDate && itemDate <= filterEndDate
		})

		setTournaments(filtredTournaments)
		setFilterPlaceholder(`${stFormatDate} - ${ndFormatDate}`)
		handleClose()
	}, [startDate, endDate, handleClose, data?.tournaments])

	const handleStartDate = useCallback((date) => { setStartDate(date) }, [])
	const handleEndDate = useCallback((date) => { setEndDate(date) }, [])

	const closeEditorComponent = () => {
		setCreateNewTournament(false)
	}

	if (isLoading) { return <h1>Loading...</h1> }
	if (error) { console.log(error) }

	return (
		<>
			<Header
				className="header-container"
				backButton={false}
				title={communityName}
				subtitle={t('Tournaments')}
				info={true}
			/>

			<div className="tournaments-wrapper">
				<div className="tournaments-container">
					{
						!createNewTournament &&
						<div className="tournaments__content">
							{
								data?.result?.length < 1 && nextRequest ?
									<div className="no-tournaments__container">
										<p className="no-tournaments__text">
											{t('Your tournaments will be presented here')}
										</p>
									</div>
									:
									<>
										<div className="tournaments-calendar__container" onClick={handleOpen}>
											<p className="tournaments-calendar__title">
												{t(filterPlaceholder)}
											</p>

											<CalendarIcon className="tournaments-calendar__icon" />
										</div>

										{
											popupIsOpen &&
											<Popup
												title={t('Filter by date')}
												onClose={handleClose}
												close={true}
											>
												<Calendar
													selected={startDate}
													onChange={(date) => handleStartDate(date)}
													selectsStart
													startDate={startDate}
													endDate={endDate}
												/>

												<Calendar
													selected={endDate}
													onChange={(date) => handleEndDate(date)}
													selectsEnd
													endDate={endDate}
													minDate={startDate}
												/>

												<div className="filter__button-container">
													<button className="filter__button" onClick={resetDate}>
														{t('Reset')}
													</button>

													<button className="filter__button" onClick={dateFilter}>
														{t('Show')}
													</button>
												</div>
											</Popup>
										}

										{
											playersData?.length < 1 && nextRequest && !playersLoading ?
												<div className="no-players__container">
													<h2>
														{t('No players yet')}
													</h2>

													<p className="no-players__description">
														{t('First, add players to your')}

														<span> </span>

														<Link to="/account">
															{t('Account')}
														</Link>

														<br />

														{t('Next, create a tournament by clicking on the button')} (+)
													</p>
												</div>
												:
												tournaments?.length < 1 &&
													<div className="no-players__container">
														<h2 style={{textAlign: 'center'}}>
															{t('To create a tournament, click on the button')} (+)
														</h2>
													</div>
										}

										<TournamentsList list={tournaments} data={data} />

										<div className="tournaments__button-addition">
											<TournamentAddition onClick={() => setCreateNewTournament(true)} />
										</div>
									</>
							}
						</div>
					}

					{
						createNewTournament &&
						<div className="tournaments__tournamnet-creation">
							<div className="tournamnet-creation__header">
								<h3 className="tournamnet-creation__title">
									{t('Tournament creation')}
								</h3>

								<Cross
									onClick={() => setCreateNewTournament(false)}
									className="tournamnet-creation__icon-cross"
								/>
							</div>

							<TournamentForm
								openedEdition={true}
								closeEditorComponent={closeEditorComponent}
								header={false}
								edit={false}
							/>
						</div>
					}
				</div>
			</div>

			<BottomMenu />
		</>
	)
}

export default Tournaments